import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { GameHeaderBar } from './GameHeaderBar';

export const SimpleAdditionGame = ({ handleClose, onGameComplete }) => {
  const [question, setQuestion] = useState({});
  const [choices, setChoices] = useState([]);
  const [score, setScore] = useState(0);
  const [questionNum, setQuestionNum] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [scoreAnim, setScoreAnim] = useState(false);

  useEffect(() => {
    const savedVolume = localStorage.getItem('simpleAddVolume');
    if (savedVolume !== null) setVolume(parseFloat(savedVolume));
    generateQuestion();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (questionNum > 10) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      // This never called onGameComplete, so - like the other games fixed
      // alongside this one - finishing the quiz didn't save progress. It's
      // also not currently reachable from GameLevelItems.js's game-id
      // switch at all (no backend game id maps to it yet); wiring this in
      // just means it'll work correctly once it is connected.
      const stars = score >= 9 ? 3 : score >= 6 ? 2 : score >= 3 ? 1 : 0;
      onGameComplete?.(stars, score * 10);
      setTimeout(() => handleClose?.(), 1800);
    }
    // eslint-disable-next-line
  }, [questionNum]);

  useEffect(() => {
    if (question.a !== undefined && !isMuted) {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(
        `What is ${question.a} plus ${question.b}?`
      );
      utter.rate = 0.9;
      utter.volume = volume;
      window.speechSynthesis.speak(utter);
    }
  }, [question, isMuted, volume]);

  const generateQuestion = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    const answer = a + b;
    let wrong1, wrong2;
    do {
      wrong1 = answer + (Math.floor(Math.random() * 5) + 1) * (Math.random() > 0.5 ? 1 : -1);
    } while (wrong1 === answer || wrong1 < 2 || wrong1 > 20);
    do {
      wrong2 = answer + (Math.floor(Math.random() * 5) + 1) * (Math.random() > 0.5 ? 1 : -1);
    } while (wrong2 === answer || wrong2 === wrong1 || wrong2 < 2 || wrong2 > 20);

    const allChoices = [answer, wrong1, wrong2].sort(() => 0.5 - Math.random());
    setQuestion({ a, b, answer });
    setChoices(allChoices);
  };

  const handleChoice = (choice) => {
    if (choice === question.answer) {
      setScore((s) => s + 1);
      setFeedback('✅ Correct!');
      setScoreAnim(true);
      setTimeout(() => setScoreAnim(false), 500);
    } else {
      setFeedback('❌ Try again!');
    }
    setTimeout(() => {
      setFeedback('');
      setQuestionNum((n) => n + 1);
      generateQuestion();
    }, 900);
  };

  if (questionNum > 10) {
    return (
      <div className="tap-letter-board-bg text-center">
        <h3>🎉 Game Over!</h3>
        <p>Your score: {score}/10</p>
      </div>
    );
  }

  return (
    <div className="tap-letter-board-bg" style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
      <GameHeaderBar
        score={score}
        questionLabel="What is:"
        question={question.a !== undefined && question.b !== undefined && <>{question.a} + {question.b} ?</>}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('simpleAddVolume', v);
        }}
        onReplay={() => {
          if (question.a !== undefined && !isMuted) {
            window.speechSynthesis.cancel();
            const utterance = new window.SpeechSynthesisUtterance(`What is ${question.a} plus ${question.b}?`);
            utterance.rate = 0.9;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
        volumeExtra={
          <button
            type="button"
            className="gh-replay"
            onClick={() => setIsMuted(m => !m)}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
        }
      />

      <div className="tap-letter-btn-grid" style={{ minHeight: 120, marginTop: 20 }}>
        {choices.map((c, i) => (
          <button
            key={i}
            className="btn btn-outline-primary m-2"
            style={{ width: 110, height: 84, fontSize: 34, fontWeight: 'bold', borderRadius: 18 }}
            onClick={() => handleChoice(c)}
          >
            {c}
          </button>
        ))}
      </div>
      <h4>{feedback}</h4>
      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 320px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 120px;
            margin-top: 10px;
            gap: 8px;
          }
          .score-pop {
            animation: correct-pop 0.5s;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 80px;
              gap: 4px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
          }
        `}
      </style>
    </div>
  );
}